import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch as apiPatch } from '@/api/client';
import { confirmModal } from '@/store/confirmStore';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Input } from '@/components/atoms/Input';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { AdBanner } from '@/components/ads/AdBanner';
import { dateTime } from '@/lib/format';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import {
  Check,
  Users,
  Gift,
  Calendar,
  AlertTriangle,
  Megaphone,
  Trophy,
  Search,
  X,
  CircleStop,
  Pencil,
} from 'lucide-react';

type ActionType =
  | 'none'
  | 'general'
  | 'random'
  | 'checkin'
  | 'write_post'
  | 'virtual'
  | 'url';

interface EventDetail {
  id: number;
  title: string;
  description: string;
  image_url: string | null;
  starts_at: string;
  ends_at: string;
  reward_points: number;
  immediate_reward_points: number;
  winner_count: number;
  max_entries: number | null;
  is_active: boolean;
  entry_count: number;
  has_joined: boolean;
  action_type?: ActionType;
  action_url?: string | null;
  post_id?: number | null;
  reward_instant?: string | null;
  reward_winner?: string | null;
  how_to_join?: string | null;
  caution?: string | null;
  announce_date?: string | null;
  winner_ids?: string[] | null;
  /** 백엔드에서 winner_ids 에 매핑된 닉네임 배열 (순서 유지) */
  winner_nicknames?: string[];
  winner_point?: number;
  instant_point?: number;
  winner_criteria?:
    | 'random'
    | 'first_come'
    | 'top_likes'
    | 'top_1'
    | 'top_3'
    | 'top_10'
    | 'admin_pick'
    | 'none';
  is_perpetual?: boolean;
  tier_rewards?: Array<{
    rank: string;
    prize_type: 'point' | 'gift';
    point_amount?: number;
    gift_text?: string;
  }>;
}

interface UserResult {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
}

/** actionType → 라벨/내비 분기 */
const ACTION_CONFIG: Record<
  Exclude<ActionType, 'none' | 'url'>,
  { label: string; path: string }
> = {
  checkin: { label: '출석체크 하러 가기', path: '/me' },
  write_post: { label: '게시글 작성하러 가기', path: '/write' },
  virtual: { label: '모의투자 하러 가기', path: '/virtual' },
  general: { label: '참여하기', path: '' },
  random: { label: '랜덤 추첨 응모', path: '' },
};

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoggedIn, me } = useAuthStore();
  const isAdmin = !!me?.adminRole;
  const { setLoginModalOpen } = useUIStore();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['events', id],
    queryFn: () => get<EventDetail>(`/events/${id}`),
    enabled: !!id,
  });

  const joinMutation = useMutation({
    mutationFn: () => post(`/events/${id}/join`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', id] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '참여에 실패했어요.'),
  });

  /** 조기 종료 (관리자 전용) — ends_at=NOW() 로 덮어 즉시 종료 */
  const endMutation = useMutation({
    mutationFn: () => apiPatch(`/events/${id}/end`),
    onSuccess: () => {
      toast.success('이벤트가 종료됐어요.');
      queryClient.invalidateQueries({ queryKey: ['events', id] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '종료 처리 실패'),
  });
  const handleEndEvent = async () => {
    const ok = await confirmModal({
      title: '이벤트 조기 종료',
      message:
        '지금 종료하시겠어요?\n종료 시각이 현재로 덮어쓰이며 더 이상 참여 불가합니다.\n(당첨자 등록은 종료 후에도 가능해요.)',
      confirmLabel: '종료',
      tone: 'danger',
    });
    if (ok) endMutation.mutate();
  };

  /* ── 당첨자 등록 (관리자 전용) — 닉네임 자동완성 칩 UI ───────── */
  const [winners, setWinners] = useState<UserResult[]>([]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [dropOpen, setDropOpen] = useState(false);
  const [winnerPoint, setWinnerPoint] = useState<number>(0);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  /** 종료 이벤트의 본문 펼침 여부 — 기본 접힘, "내용 보기" 클릭 시 노출 */
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node))
        setDropOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const { data: userResults } = useQuery({
    queryKey: ['users', 'search', debounced],
    queryFn: () =>
      get<{ list: UserResult[] }>(
        `/users/search?q=${encodeURIComponent(debounced)}&limit=8`,
      ),
    enabled: isAdmin && dropOpen && debounced.length >= 2,
    staleTime: 30_000,
  });

  const addWinner = (u: UserResult) => {
    if (winners.some((w) => w.user_id === u.user_id)) return;
    setWinners((prev) => [...prev, u]);
    setSearch('');
    setDropOpen(false);
  };
  const removeWinner = (userId: string) => {
    setWinners((prev) => prev.filter((w) => w.user_id !== userId));
  };

  const winnersMutation = useMutation({
    mutationFn: (payload: { winnerNicknames: string[]; winnerPoint: number }) =>
      post(`/events/${id}/winners`, payload),
    onSuccess: (res: any) => {
      toast.success(
        `${res?.data?.winners?.length ?? winners.length}명 당첨 등록 완료 · ${
          res?.data?.point?.toLocaleString?.('ko-KR') ?? 0
        }P 지급`,
      );
      setWinners([]);
      setWinnerPoint(0);
      queryClient.invalidateQueries({ queryKey: ['events', id] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '당첨자 등록 실패'),
  });

  if (isLoading) return <Skeleton className="m-5 h-96" />;
  if (!data) return null;

  const ended = new Date(data.ends_at).getTime() < Date.now();
  const ongoing = data.is_active && !ended;
  const actionType: ActionType = data.action_type ?? 'general';
  /* 'none' 은 버튼 자체 숨김, 'url' 은 커스텀 URL, 그 외는 기존 ACTION_CONFIG 분기 */
  const hasJoinButton = actionType !== 'none';
  const actionConfig =
    actionType !== 'none' && actionType !== 'url'
      ? ACTION_CONFIG[actionType]
      : null;

  const handleClick = async () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (!data.has_joined) {
      try {
        await joinMutation.mutateAsync();
      } catch {
        return;
      }
    }
    /* url 타입: 새 탭으로 외부 URL 이동 (내부 경로면 navigate 로) */
    if (actionType === 'url') {
      const url = data.action_url ?? '';
      if (!url) {
        toast.success('이벤트 참여가 완료되었어요! 🎉');
        return;
      }
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
      return;
    }
    if (actionConfig?.path) {
      navigate(actionConfig.path);
    } else {
      toast.success('이벤트 참여가 완료되었어요! 🎉');
    }
  };

  const handleWinnerSubmit = () => {
    if (winners.length === 0) {
      toast.error('당첨자를 한 명 이상 선택해 주세요.');
      return;
    }
    winnersMutation.mutate({
      winnerNicknames: winners.map((w) => w.nickname),
      winnerPoint,
    });
  };

  const buttonLabel = (() => {
    if (!ongoing) return '종료된 이벤트';
    if (actionType === 'url') return data.action_url ? '참여하러 가기' : '참여하기';
    if (data.has_joined && actionConfig?.path) return actionConfig.label;
    if (data.has_joined && !actionConfig?.path) return '참여 완료';
    return actionConfig?.label ?? '참여하기';
  })();

  return (
    <div className="container-base py-6">
      <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {ongoing ? <Badge variant="rise">진행 중</Badge> : <Badge>종료</Badge>}
          {data.reward_points > 0 && (
            <Badge variant="editor">🎁 최대 {data.reward_points.toLocaleString('ko-KR')}P</Badge>
          )}
          {data.has_joined && (
            <Badge variant="primary">
              <Check className="h-3 w-3" />
              참여 완료
            </Badge>
          )}
        </div>

        <h1 className="text-2xl font-extrabold leading-tight">{data.title}</h1>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[var(--color-text-sub)]">
          <span>
            📅{' '}
            {data.is_perpetual
              ? `${dateTime(data.starts_at)} · 상시 진행`
              : `${dateTime(data.starts_at)} ~ ${dateTime(data.ends_at)}`}
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {data.entry_count.toLocaleString('ko-KR')}명 참여 중
          </span>
        </div>

        {/* 표시 가능한 winner_criteria — 'admin_pick' / 'none' 은 노출 의미 없어 컨테이너 트리거에서도 제외 */}
        {(() => {
          const hasCriteriaToShow =
            !!data.winner_criteria &&
            data.winner_criteria !== 'admin_pick' &&
            data.winner_criteria !== 'none';
          const showInfoBox =
            data.reward_instant ||
            data.reward_winner ||
            data.how_to_join ||
            data.announce_date ||
            data.caution ||
            hasCriteriaToShow;
          if (!showInfoBox) return null;
          return (
          <section className="mt-4 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-sm">
            {hasCriteriaToShow && (
                <InfoRow
                  icon={Trophy}
                  label="당첨 기준"
                  value={criteriaLabel(data.winner_criteria!, data.winner_count)}
                />
              )}
            {data.reward_instant && (
              <InfoRow icon={Gift} label="참여 즉시 보상" value={data.reward_instant} />
            )}
            {data.reward_winner && (
              <InfoRow icon={Trophy} label="당첨 선물" value={data.reward_winner} />
            )}
            {data.how_to_join && (
              <InfoRow icon={Megaphone} label="참여 방법" value={data.how_to_join} />
            )}
            {data.announce_date && (
              <InfoRow
                icon={Calendar}
                label="발표일"
                value={data.announce_date.slice(0, 10)}
              />
            )}
            {data.caution && (
              <InfoRow icon={AlertTriangle} label="주의사항" value={data.caution} />
            )}
          </section>
          );
        })()}

        {/* 등수별 보상 — top_1/top_3/top_10 criteria 에서 저장된 tier_rewards 노출.
            winner_criteria='none' (당첨자 없음) 인 경우엔 DB 에 잔존 tier_rewards 가 있더라도 숨김. */}
        {data.winner_criteria !== 'none' &&
          Array.isArray(data.tier_rewards) &&
          data.tier_rewards.length > 0 && (
          <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-sm">
            <div className="mb-2 flex items-center gap-1.5 font-semibold">
              <Trophy className="h-4 w-4 text-[var(--color-primary)]" />
              등수별 보상
            </div>
            <ul className="space-y-1.5">
              {data.tier_rewards.map((t, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] pb-1.5 last:border-0 last:pb-0"
                >
                  <span className="font-semibold text-[var(--color-text)]">
                    {i < 3 && <span className="mr-1">{['🥇', '🥈', '🥉'][i]}</span>}
                    {t.rank}
                  </span>
                  <span className="num text-[var(--color-text-sub)]">
                    {t.prize_type === 'point'
                      ? `${(t.point_amount ?? 0).toLocaleString('ko-KR')}P`
                      : t.gift_text || '미정'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {((data.instant_point ?? data.immediate_reward_points) > 0 ||
          (data.winner_point ?? data.reward_points) > 0) && (
          <div className="mt-4 rounded-xl bg-[var(--color-primary)]/5 p-4 text-sm">
            <div className="mb-1 flex items-center gap-1.5 font-semibold">
              <Gift className="h-4 w-4 text-[var(--color-primary)]" />
              포인트 안내
            </div>
            {(data.instant_point ?? data.immediate_reward_points) > 0 && (
              <div className="num text-[var(--color-text-sub)]">
                · 참여 즉시 +
                {(data.instant_point ?? data.immediate_reward_points).toLocaleString('ko-KR')}P
              </div>
            )}
            {(data.winner_point ?? data.reward_points) > 0 && (
              <div className="num text-[var(--color-text-sub)]">
                · 당첨 시 +
                {(data.winner_point ?? data.reward_points).toLocaleString('ko-KR')}P
                {data.winner_count > 0 && ` (${data.winner_count}명 추첨)`}
              </div>
            )}
          </div>
        )}

        {/* 종료 이벤트 — 당첨자를 본문 위 상단에 강조 표시 */}
        {!ongoing &&
          Array.isArray(data.winner_nicknames) &&
          data.winner_nicknames.length > 0 && (
            <section className="mt-6 rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/[0.04] p-4">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[var(--color-primary)]">
                <Trophy className="h-4 w-4" />
                당첨자 발표 · {data.winner_nicknames.length}명
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.winner_nicknames.map((nick, i) => (
                  <span
                    key={`${nick}-${i}`}
                    className="inline-flex items-center rounded-full bg-[var(--color-surface)] px-3 py-1 text-sm font-medium text-[var(--color-text)]"
                  >
                    {i < 3 && (
                      <span className="mr-1 text-xs">
                        {['🥇', '🥈', '🥉'][i]}
                      </span>
                    )}
                    {nick}
                  </span>
                ))}
              </div>
              {(data.winner_point ?? data.reward_points) > 0 && (
                <p className="mt-2 num text-xs text-[var(--color-text-sub)]">
                  당첨 포인트 +
                  {(data.winner_point ?? data.reward_points).toLocaleString('ko-KR')}P 지급 완료
                </p>
              )}
            </section>
          )}

        {/* 이벤트 본문 — Tiptap HTML (어드민 작성).
            빈 줄은 <p></p> 빈 단락으로 저장되는데 브라우저 기본 height 0 이라 안 보임.
            [&_p:empty]:before:content-['\200B'] 와 min-h-[1em] 조합으로 시각 줄바꿈 확보. */}
        {ongoing ? (
          <div
            className="prose prose-sm mt-6 max-w-none [&_p:empty]:min-h-[1em] [&_p:empty]:before:content-['\200B']"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        ) : (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowContent((v) => !v)}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-sub)] transition-colors hover:border-[var(--color-primary)]/40 hover:text-[var(--color-text)]"
            >
              {showContent ? '내용 숨기기' : '내용 보기'}
            </button>
            {showContent && (
              <div
                className="prose prose-sm mt-4 max-w-none [&_p:empty]:min-h-[1em] [&_p:empty]:before:content-['\200B']"
                dangerouslySetInnerHTML={{ __html: data.description }}
              />
            )}
          </div>
        )}

        {/* 참여 버튼 — actionType='none' 이면 숨김 */}
        {hasJoinButton && (
          <Button
            fullWidth
            size="lg"
            className="mt-6"
            disabled={!ongoing}
            loading={joinMutation.isPending}
            variant={data.has_joined && actionType !== 'url' ? 'secondary' : 'primary'}
            onClick={handleClick}
          >
            {buttonLabel}
          </Button>
        )}
        {hasJoinButton && data.has_joined && actionType === 'general' && (
          <p className="mt-2 text-center text-xs text-[var(--color-text-sub)]">
            이미 참여하셨어요.
          </p>
        )}
      </article>

      {/* 관리자 액션 바 — 수정(항상) + 조기 종료(진행 중) */}
      {isAdmin && (
        <section className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">관리자 액션</span>
            <span className="text-xs text-[var(--color-text-sub)]">
              이벤트 수정 및 종료를 처리할 수 있어요.
            </span>
          </div>
          <div className="flex items-center gap-2">
            {data.post_id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/write?edit=${data.post_id}`)}
              >
                <Pencil className="h-4 w-4" />
                수정
              </Button>
            )}
            {ongoing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEndEvent}
                loading={endMutation.isPending}
                className="!text-[var(--color-rise)]"
              >
                <CircleStop className="h-4 w-4" />
                조기 종료
              </Button>
            )}
          </div>
        </section>
      )}

      {/* 당첨자 등록 (관리자 전용) — 닉네임 자동완성 + 칩 */}
      {isAdmin && (
        <section className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <h2 className="section-title mb-3 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[var(--color-primary)]" />
            당첨자 등록 (관리자)
          </h2>
          <p className="mb-4 text-xs text-[var(--color-text-sub)]">
            닉네임을 2글자 이상 입력해 검색 후 선택. 여러 명 추가 가능 · 선택된 유저에게
            포인트 자동 지급 및 알림 발송.
          </p>

          {Array.isArray(data.winner_nicknames) && data.winner_nicknames.length > 0 && (
            <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm text-[var(--color-text-sub)]">
              <div className="font-semibold text-[var(--color-text)]">
                기존 당첨자 · {data.winner_nicknames.length}명
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.winner_nicknames.map((nick, i) => (
                  <span
                    key={`${nick}-${i}`}
                    className="inline-flex items-center rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[var(--color-text)]"
                  >
                    {nick}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 선택된 당첨자 칩 */}
          {winners.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {winners.map((w) => (
                <span
                  key={w.user_id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-sm"
                >
                  <Avatar src={w.profile_image_url ?? undefined} alt={w.nickname} size="xs" />
                  <span className="font-medium">{w.nickname}</span>
                  <LevelBadge level={w.level} size="sm" />
                  <button
                    type="button"
                    onClick={() => removeWinner(w.user_id)}
                    className="ml-0.5 rounded-full p-0.5 text-[var(--color-text-sub)] hover:bg-[var(--color-fall)]/10 hover:text-[var(--color-fall)]"
                    aria-label={`${w.nickname} 제거`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* 닉네임 검색 입력 + 드롭다운 */}
          <div ref={searchWrapRef} className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setDropOpen(true);
              }}
              onFocus={() => setDropOpen(true)}
              placeholder="당첨자 닉네임 검색 (2글자 이상)"
              className="pl-9"
            />
            {dropOpen && debounced.length >= 2 && (
              <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
                {(userResults?.list ?? []).length === 0 ? (
                  <li className="px-3 py-3 text-center text-xs text-[var(--color-text-sub)]">
                    검색 결과가 없어요.
                  </li>
                ) : (
                  userResults!.list.map((u) => {
                    const picked = winners.some((w) => w.user_id === u.user_id);
                    return (
                      <li key={u.user_id}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (!picked) addWinner(u);
                          }}
                          disabled={picked}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-primary)]/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                        >
                          <Avatar
                            src={u.profile_image_url ?? undefined}
                            alt={u.nickname}
                            size="sm"
                          />
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {u.nickname}
                          </span>
                          <LevelBadge level={u.level} size="sm" />
                          {picked && (
                            <span className="text-[11px] text-[var(--color-text-sub)]">
                              이미 선택됨
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>

          <div className="mb-3 max-w-xs">
            <label className="mb-1.5 block text-sm font-medium">당첨 포인트</label>
            <Input
              type="number"
              min={0}
              max={1000000}
              value={winnerPoint || data.winner_point || 0}
              onChange={(e) => setWinnerPoint(Number(e.target.value) || 0)}
              placeholder="0"
            />
            <p className="mt-1 text-xs text-[var(--color-text-sub)]">
              미입력 시 이벤트에 등록된 당첨 포인트
              ({(data.winner_point ?? 0).toLocaleString('ko-KR')}P)가 지급돼요.
            </p>
          </div>
          <Button
            onClick={handleWinnerSubmit}
            loading={winnersMutation.isPending}
            disabled={winners.length === 0}
          >
            당첨자 등록 및 포인트 지급 ({winners.length}명)
          </Button>
        </section>
      )}

      {/* 페이지 하단 광고 — multiplex-h 전역 비활성화로 display-rect 로 교체 (2026-05-29) */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
    </div>
  );
}

/** winner_criteria 라벨 변환 */
function criteriaLabel(
  c:
    | 'random'
    | 'first_come'
    | 'top_likes'
    | 'top_1'
    | 'top_3'
    | 'top_10'
    | 'admin_pick'
    | 'none',
  count?: number,
): string {
  switch (c) {
    case 'random':
      return '랜덤 추첨';
    case 'first_come':
      return count && count > 0 ? `선착순 ${count.toLocaleString('ko-KR')}명` : '선착순';
    case 'top_likes':
      return '추천순';
    case 'top_1':
      return '1등 (단독)';
    case 'top_3':
      return 'TOP 3 (1·2·3등)';
    case 'top_10':
      return 'TOP 10';
    case 'admin_pick':
      return '관리자 지정';
    case 'none':
      return '당첨자 없음';
  }
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-sub)]" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-[var(--color-text-sub)]">{label}</div>
        <div className="mt-0.5 whitespace-pre-wrap break-words text-[var(--color-text)]">
          {value}
        </div>
      </div>
    </div>
  );
}
